import { useQuery } from "@tanstack/react-query";
import api from "@/lib/axios";
import { endpoints } from "@/lib/utils/copyAndNaming/endpoints";
import { Account } from "@/features/account/types/account.types";
import {
  getCookie,
  getSessionStorage,
  setSessionStorage,
} from "@/lib/utils/queryHelpers";

export const getCurrentAccount = async (): Promise<Account | null> => {
  const { data } = await api
    .get(`/api/${endpoints.accounts}/me`)
    .catch((error) => {
      // If the user is not found, return null
      if (error.response.status === 404) {
        return { data: null };
      }
      // If the user is not authenticated, redirect to login
      if (error.response.status === 401) {
        return { data: null };
      }
      throw error;
    });

  // Retrieve from url in the form of /sp/:spaceId to get the space id
  let spaceId = getSessionStorage("currentSpaceId");
  const path = window.location.pathname.split("/");
  const spaceIdIndex = path.indexOf("sp");
  if (spaceIdIndex !== -1) {
    spaceId = path[spaceIdIndex + 1];
    setSessionStorage("currentSpaceId", spaceId);
  }
  if (!spaceId) {
    spaceId = data.defaultSpace.value;
    setSessionStorage("currentSpaceId", spaceId ?? "");
  }

  data.currentSpace = spaceId;
  api.defaults.headers.common["X-Tenant-Id"] = data.currentSpace;

  // Rewrite the url to include the space id
  // If the last part of the url is welcome or error, don't include the space id
  if (
    spaceIdIndex === -1 &&
    !["welcome", "error", "me"].includes(path[path.length - 1])
  ) {
    window.history.replaceState(
      null,
      "",
      `/sp/${spaceId}${window.location.pathname}`
    );
  }

  // Get the current user space info
  const { data: currentSpaceData } = await api
    .get(`/api/${endpoints.users}/me`)
    .catch((error) => {
      // If the user is not found, return null
      if (error.response.status === 404) {
        return { data: null };
      }
      // If the user is not authenticated, redirect to login
      if (error.response.status === 401) {
        return { data: null };
      }
      throw error;
    });
  data.username = currentSpaceData.user;
  data.organization = currentSpaceData.organization;

  // Get the impersonated user
  const impersonatedUid = getCookie("impersonatedUid");
  if (impersonatedUid) {
    api.defaults.headers.common["X-Impersonated-Uid"] = impersonatedUid;
    data.impersonatedUid = impersonatedUid;
    data.impersonatedUsername = getCookie("impersonatedUsername");
  }

  return data;
};

type Options = {
  enabled: boolean;
};

export const useGetCurrentAccount = (options?: Options) => {
  return useQuery({
    queryKey: [endpoints.accounts, "me"],
    queryFn: () => getCurrentAccount(),
    enabled: options?.enabled ?? true,
  });
};
